import { useEffect, useState } from "react";
import { Link as RouterLink, useLocation } from "react-router-dom";
import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  Divider,
  Link,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import TableRowsIcon from "@mui/icons-material/TableRows";
import ViewModuleIcon from "@mui/icons-material/ViewModule";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import type {
  CorporateListPagination,
  CorporateListRow,
  ListRegistryTag,
} from "../types";
import { NO_CORPORATE_NAME_LABEL, PUBLIC_REGISTRY_LABEL } from "../constants";
import { RegistryTagList } from "./registryTags/RegistryTagList";
import { prioritizeSelectedTags } from "../utils/registryTags";
import { toStrList } from "../utils/normalize";
import {
  EMPTY_DISPLAY,
  formatNumberOrDash,
  formatYenCompact,
} from "../utils/format";
import {
  buildAcquireUrl,
  corporateMasterDetailPath,
  type CorporateMasterDetailState,
} from "../utils/paths";
import { ReadOnlyTooltip } from "@/components/Tooltip/ReadOnlyTooltip";
import { useReadOnlyUser } from "@/hooks/useReadOnlyUser";
import { useIsMobile } from "@/hooks/useIsMobile";
import type {
  CorporateSelection,
  CorporateSelectionEntry,
} from "../hooks/useCorporateSelection";

type ViewMode = "table" | "card";

const VIEW_MODE_STORAGE_KEY = "corporateMaster.viewMode";

// localStorageは読み書きとも失敗しうる（プライベートモード等）ためtry/catchで包む。
const readStoredViewMode = (): ViewMode => {
  try {
    return localStorage.getItem(VIEW_MODE_STORAGE_KEY) === "card"
      ? "card"
      : "table";
  } catch {
    return "table";
  }
};

type SegmentTone = "listed" | "unlisted" | "unknown";

interface Row {
  corporateNumber: string;
  /** 検索結果の中での位置（ページをまたいだ並び順） */
  position: number;
  name: string;
  segment: string;
  segmentTone: SegmentTone;
  marketSegment: string | null;
  location: string | null;
  industries: string[];
  capital: string;
  employees: string;
  netSales: string;
  representative: string | null;
  updateDate: string;
  establishmentYear: string;
  foundingYear: string;
  detailPath: string;
  acquirePath: string;
  registryTags: ListRegistryTag[];
}

// gBiz要約の有無で「非上場（属性あり）」と「情報未取得」を区別する。
const hasGbiz = (item: CorporateListRow): boolean =>
  item.kindLabel != null ||
  item.capitalStock != null ||
  item.employeeNumber != null ||
  item.representativeName != null ||
  (item.industry?.length ?? 0) > 0;

// APIの一覧行を表示用の素片に落とす。テーブル・カード双方で共有する。
const toRow = (
  item: CorporateListRow,
  selectedRegistryTagCodes: readonly string[],
  position: number
): Row => {
  const listed = item.isListed;
  return {
    corporateNumber: item.corporateNumber,
    position,
    name: item.name ?? "",
    segment: listed ? "上場" : hasGbiz(item) ? "非上場" : "情報未取得",
    segmentTone: listed ? "listed" : hasGbiz(item) ? "unlisted" : "unknown",
    marketSegment: item.marketSegment ?? null,
    location:
      [item.prefectureName, item.cityName].filter(Boolean).join("") || null,
    industries: toStrList(item.industry),
    capital: formatYenCompact(item.capitalStock),
    employees: formatNumberOrDash(item.employeeNumber, "人"),
    netSales: formatYenCompact(item.netSales),
    representative: item.representativeName ?? null,
    updateDate: item.updateDate ?? EMPTY_DISPLAY,
    establishmentYear:
      item.establishmentYear != null
        ? `${item.establishmentYear}年`
        : EMPTY_DISPLAY,
    foundingYear:
      item.foundingYear != null ? `${item.foundingYear}年` : EMPTY_DISPLAY,
    detailPath: corporateMasterDetailPath(item.corporateNumber),
    acquirePath: buildAcquireUrl(item.corporateNumberLast12, item.name),
    registryTags: prioritizeSelectedTags(
      item.registryTags ?? [],
      selectedRegistryTagCodes
    ),
  };
};

const SEGMENT_COLOR: Record<SegmentTone, "primary" | "default" | "success"> = {
  listed: "primary",
  unlisted: "default",
  unknown: "default",
};

// 詳細から「法人マスタ一覧へ」で戻ったとき、今の検索条件で一覧を出し直すため、一覧のURLクエリを渡す。
const useDetailState = (): CorporateMasterDetailState => ({
  listSearch: useLocation().search,
});

const NameLink: React.FC<{ row: Row }> = ({ row }) => {
  const detailState = useDetailState();
  return (
    <Link component={RouterLink} to={row.detailPath} state={detailState}>
      {row.name || NO_CORPORATE_NAME_LABEL}
    </Link>
  );
};

const AcquireButton: React.FC<{ row: Row }> = ({ row }) => {
  // 閲覧専用ユーザーは取得できないため押せないようにする（詳細画面と同じ）
  const isReadOnly = useReadOnlyUser();
  return (
    <ReadOnlyTooltip isReadOnly={isReadOnly}>
      <Button
        component={RouterLink}
        to={row.acquirePath}
        startIcon={<DescriptionOutlinedIcon />}
        variant="outlined"
        size="small"
        sx={{ borderRadius: 2, whiteSpace: "nowrap" }}
        disabled={isReadOnly}
      >
        取得
      </Button>
    </ReadOnlyTooltip>
  );
};

const DetailButton: React.FC<{ row: Row }> = ({ row }) => {
  const detailState = useDetailState();
  return (
    <Button
      component={RouterLink}
      to={row.detailPath}
      state={detailState}
      variant="text"
      size="small"
      sx={{ whiteSpace: "nowrap" }}
    >
      詳細
    </Button>
  );
};

const toEntry = (row: Row): CorporateSelectionEntry => ({
  corporateNumber: row.corporateNumber,
  position: row.position,
});

const RowCheckbox: React.FC<{ row: Row; selection: CorporateSelection }> = ({
  row,
  selection,
}) => {
  const isMobile = useIsMobile();
  return (
    <Checkbox
      // SP はタップ領域を確保するため既定の大きさにする
      size={isMobile ? "medium" : "small"}
      checked={selection.selected.has(row.corporateNumber)}
      onChange={(event) => {
        selection.toggle([toEntry(row)], event.target.checked);
      }}
      inputProps={{
        "aria-label": `${row.name || NO_CORPORATE_NAME_LABEL}を選択`,
      }}
    />
  );
};

const PageCheckbox: React.FC<{
  rows: Row[];
  selection: CorporateSelection;
}> = ({ rows, selection }) => {
  const selectedOnPage = rows.filter((row) =>
    selection.selected.has(row.corporateNumber)
  ).length;
  return (
    <Checkbox
      size="small"
      checked={rows.length > 0 && selectedOnPage === rows.length}
      indeterminate={selectedOnPage > 0 && selectedOnPage < rows.length}
      disabled={rows.length === 0}
      onChange={(event) => {
        selection.toggle(rows.map(toEntry), event.target.checked);
      }}
      inputProps={{ "aria-label": "このページの法人をすべて選択" }}
    />
  );
};

const TableView: React.FC<{
  rows: Row[];
  selection?: CorporateSelection;
}> = ({ rows, selection }) => (
  <TableContainer>
    <Table
      size="small"
      aria-label="法人情報一覧（テーブル）"
      // 選択欄の分だけ狭くなっても法人名が縦に折れないよう、法人名に幅を取り、短い列は折り返さない
      // （収まらないときは横にスクロールする）
      sx={{
        "& .MuiTableCell-root": { whiteSpace: "nowrap" },
        "& .MuiTableCell-root.wrap": { whiteSpace: "normal" },
      }}
    >
      <TableHead>
        <TableRow>
          {selection ? (
            <TableCell padding="checkbox">
              <PageCheckbox rows={rows} selection={selection} />
            </TableCell>
          ) : null}
          <TableCell className="wrap" sx={{ minWidth: 200 }}>
            法人名
          </TableCell>
          <TableCell>法人番号</TableCell>
          <TableCell>区分</TableCell>
          <TableCell>更新日</TableCell>
          <TableCell>設立年</TableCell>
          <TableCell>創業年</TableCell>
          <TableCell>{PUBLIC_REGISTRY_LABEL}</TableCell>
          <TableCell align="right">操作</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map((row) => (
          <TableRow
            key={row.corporateNumber}
            hover
            selected={selection?.selected.has(row.corporateNumber) ?? false}
          >
            {selection ? (
              <TableCell padding="checkbox">
                <RowCheckbox row={row} selection={selection} />
              </TableCell>
            ) : null}
            <TableCell className="wrap">
              <NameLink row={row} />
            </TableCell>
            <TableCell>{row.corporateNumber}</TableCell>
            <TableCell>{row.segment}</TableCell>
            <TableCell>{row.updateDate}</TableCell>
            <TableCell>{row.establishmentYear}</TableCell>
            <TableCell>{row.foundingYear}</TableCell>
            <TableCell className="wrap">
              {row.registryTags.length ? (
                <RegistryTagList tags={row.registryTags} />
              ) : (
                EMPTY_DISPLAY
              )}
            </TableCell>
            <TableCell align="right">
              <Stack
                direction="row"
                spacing={0.5}
                justifyContent="flex-end"
                alignItems="center"
              >
                <DetailButton row={row} />
                <AcquireButton row={row} />
              </Stack>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </TableContainer>
);

const StatTile: React.FC<{ label: string; value: string }> = ({
  label,
  value,
}) => (
  <Box
    sx={{
      flex: 1,
      minWidth: 0,
      px: 1,
      py: 0.75,
      borderRadius: 1,
      bgcolor: "action.hover",
      textAlign: "center",
    }}
  >
    <Typography variant="caption" color="text.secondary" display="block">
      {label}
    </Typography>
    <Typography variant="body2" fontWeight="bold" noWrap>
      {value}
    </Typography>
  </Box>
);

const IconLine: React.FC<{
  icon: React.ReactNode;
  text: string;
}> = ({ icon, text }) => (
  <Stack
    direction="row"
    spacing={0.75}
    alignItems="center"
    sx={{ minWidth: 0 }}
  >
    <Box sx={{ color: "text.secondary", display: "flex" }}>{icon}</Box>
    <Typography variant="body2" color="text.secondary" noWrap>
      {text}
    </Typography>
  </Stack>
);

const CorporateCard: React.FC<{
  row: Row;
  selection?: CorporateSelection;
}> = ({ row, selection }) => (
  <Card
    variant="elevation"
    elevation={3}
    sx={{
      height: "100%",
      transition: "box-shadow 0.2s, transform 0.2s",
      "&:hover": { boxShadow: 8, transform: "translateY(-2px)" },
    }}
  >
    <CardContent sx={{ height: "100%" }}>
      <Stack spacing={1.25} sx={{ height: "100%" }}>
        {/* 区分バッジ・市場区分 */}
        <Stack
          direction="row"
          spacing={0.75}
          alignItems="center"
          flexWrap="wrap"
          useFlexGap
        >
          <Chip
            label={row.segment}
            size="small"
            color={SEGMENT_COLOR[row.segmentTone]}
            variant={row.segmentTone === "listed" ? "filled" : "outlined"}
            sx={{ borderRadius: 0.5 }}
          />
          {row.marketSegment && (
            <Chip
              label={row.marketSegment}
              size="small"
              variant="outlined"
              sx={{ borderRadius: 0.5 }}
            />
          )}
          {selection ? (
            <Box sx={{ ml: "auto", my: -1, mr: -1 }}>
              <RowCheckbox row={row} selection={selection} />
            </Box>
          ) : null}
        </Stack>

        {/* 社名・法人番号 */}
        <Box sx={{ minWidth: 0 }}>
          <Typography
            component="div"
            variant="subtitle1"
            fontWeight="bold"
            sx={{ lineHeight: 1.3 }}
          >
            <NameLink row={row} />
          </Typography>
          <Typography variant="caption" color="text.secondary">
            法人番号 {row.corporateNumber}
          </Typography>
        </Box>

        {/* 所在地・代表者 */}
        <Stack spacing={0.5}>
          {row.location && (
            <IconLine
              icon={<LocationOnOutlinedIcon fontSize="small" />}
              text={row.location}
            />
          )}
          {row.representative && (
            <IconLine
              icon={<PersonOutlineIcon fontSize="small" />}
              text={`代表者 ${row.representative}`}
            />
          )}
        </Stack>

        {/* 指標タイル */}
        <Stack direction="row" spacing={1}>
          <StatTile label="資本金" value={row.capital} />
          <StatTile label="従業員" value={row.employees} />
          <StatTile label="売上高" value={row.netSales} />
        </Stack>

        {/* 業種タグ */}
        {row.industries.length > 0 && (
          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
            {row.industries.slice(0, 4).map((industry) => (
              <Chip
                key={industry}
                label={industry}
                size="small"
                variant="outlined"
                sx={{ bgcolor: "action.hover", borderRadius: 0.5 }}
              />
            ))}
          </Stack>
        )}

        {/* 公表情報のタグ */}
        <RegistryTagList tags={row.registryTags} />

        {/* フッタ */}
        <Box sx={{ mt: "auto" }}>
          <Divider sx={{ mb: 1 }} />
          <Stack direction="row" alignItems="center">
            <Typography variant="caption" color="text.secondary">
              更新 {row.updateDate}・設立 {row.establishmentYear}・創業{" "}
              {row.foundingYear}
            </Typography>
            <Box sx={{ flex: 1 }} />
            <DetailButton row={row} />
            <AcquireButton row={row} />
          </Stack>
        </Box>
      </Stack>
    </CardContent>
  </Card>
);

const CardView: React.FC<{
  rows: Row[];
  selection?: CorporateSelection;
}> = ({ rows, selection }) => (
  // MUI Gridの負マージンで右端がはみ出すのを避けるためCSS Gridで等間隔配置する。
  <Box
    sx={{
      display: "grid",
      gap: 2,
      gridTemplateColumns: {
        xs: "1fr",
        sm: "repeat(2, 1fr)",
        lg: "repeat(3, 1fr)",
      },
    }}
  >
    {rows.map((row) => (
      <CorporateCard
        key={row.corporateNumber}
        row={row}
        selection={selection}
      />
    ))}
  </Box>
);

interface Props {
  items: CorporateListRow[];
  // 条件一致の総件数（表示上限で切られる前の数）と表示上限。ヘッダの件数表示に使う。
  totalCount: number;
  limit: number;
  /** 条件なしの最新1000件はAPI側でページを切り出す。 */
  serverPagination?: CorporateListPagination;
  /** 検索条件で選んだ公表情報のタグ。一覧では先に並べ、「ほかN」に隠さない。 */
  selectedRegistryTagCodes?: readonly string[];
  /** 指定すると各法人に選択欄を出す */
  selection?: CorporateSelection;
}

const ROWS_PER_PAGE_OPTIONS = [20, 50, 100];

export const CorporateMasterResults: React.FC<Props> = ({
  items,
  totalCount,
  limit,
  serverPagination,
  selectedRegistryTagCodes = [],
  selection,
}) => {
  const [viewMode, setViewMode] = useState<ViewMode>(readStoredViewMode);
  // SPは検索項目・一覧が縦に長くなるため、法人一覧はカードのみに固定しトグルを隠す。
  const isMobile = useIsMobile();
  const effectiveViewMode: ViewMode = isMobile ? "card" : viewMode;
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(ROWS_PER_PAGE_OPTIONS[0]);

  useEffect(() => {
    try {
      localStorage.setItem(VIEW_MODE_STORAGE_KEY, viewMode);
    } catch {
      // 保存失敗はセッション内表示に影響しないため無視する。
    }
  }, [viewMode]);

  // 条件なしの一覧は API がページを切り出すため、位置はページ番号から足して一覧の先頭からの通し番号にする。
  const positionOffset = serverPagination
    ? serverPagination.page * serverPagination.pageSize
    : 0;
  const rows = items.map((item, index) =>
    toRow(item, selectedRegistryTagCodes, positionOffset + index)
  );

  // 検索結果が差し替わったら（件数が同じでも）先頭ページへ戻す。範囲外ページの空表示や、
  // 2ページ目以降のまま別条件の結果を見て先頭の法人を見落とすことを防ぐ。
  // 取得前の空配列は毎回新しい参照になるため、参照ではなく結果の識別子の並びで差し替わりを判定する。
  const resultsKey = items.map((item) => item.corporateNumber).join(",");
  useEffect(() => {
    setPage(0);
  }, [resultsKey]);

  const pagedRows = serverPagination
    ? rows
    : rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const paginationControls = (
    <TablePagination
      component="div"
      count={serverPagination ? totalCount : rows.length}
      page={serverPagination?.page ?? page}
      onPageChange={(_event, next) => {
        if (serverPagination) serverPagination.onPageChange(next);
        else setPage(next);
      }}
      rowsPerPage={serverPagination?.pageSize ?? rowsPerPage}
      rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
      onRowsPerPageChange={(event) => {
        if (serverPagination)
          serverPagination.onPageSizeChange(Number(event.target.value));
        else {
          setRowsPerPage(Number(event.target.value));
          setPage(0);
        }
      }}
      labelRowsPerPage="表示件数"
      labelDisplayedRows={({ from, to, count }) =>
        `${count.toLocaleString("ja-JP")}件中 ${from.toLocaleString(
          "ja-JP"
        )}–${to.toLocaleString("ja-JP")}件`
      }
    />
  );

  return (
    <Stack spacing={1.5}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
          flexWrap: "wrap",
        }}
      >
        <Typography role="status" variant="subtitle1" fontWeight="bold">
          {/* BEは件数を limit+1 件で数え打ちを止めるため、limit 超は「以上」で表す */}
          {totalCount > limit ? (
            <>
              {`検索結果：${limit.toLocaleString("ja-JP")}件以上`}
              <Typography
                component="span"
                variant="body2"
                color="text.secondary"
                sx={{ display: "block" }}
              >
                {`（上位${limit.toLocaleString(
                  "ja-JP"
                )}件を表示。条件を絞り込んでください）`}
              </Typography>
            </>
          ) : (
            `検索結果：${totalCount.toLocaleString("ja-JP")}件`
          )}
        </Typography>
        {!isMobile && (
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            size="small"
            aria-label="表示形式"
            onChange={(_event, next: ViewMode | null) => {
              if (next) setViewMode(next);
            }}
          >
            <ToggleButton value="table" aria-label="テーブル表示">
              <TableRowsIcon fontSize="small" sx={{ mr: 0.5 }} />
              テーブル
            </ToggleButton>
            <ToggleButton value="card" aria-label="カード表示">
              <ViewModuleIcon fontSize="small" sx={{ mr: 0.5 }} />
              カード
            </ToggleButton>
          </ToggleButtonGroup>
        )}
      </Box>
      <Divider />
      {rows.length === 0 ? (
        <Typography sx={{ py: 2 }}>法人が見つかりません</Typography>
      ) : (
        <>
          {paginationControls}
          {effectiveViewMode === "table" ? (
            <TableView rows={pagedRows} selection={selection} />
          ) : (
            <CardView rows={pagedRows} selection={selection} />
          )}
          {paginationControls}
        </>
      )}
    </Stack>
  );
};
